/**
 * To Your Door Barrow — Theme JavaScript
 * Postcode gate, cart drawer, qty controls, delivery countdown
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'tydb_postcode';
  const BROWSE_KEY = 'tydb_browsing';
  const FAV_KEY = 'tydb_favourites';

  const Theme = {
    settings: window.themeSettings || {},

    init() {
      this.cacheDom();
      this.bindGlobal();
      this.initPostcodeGate();
      this.initHeader();
      this.initCartDrawer();
      this.initQuantityControls();
      this.initProductTabs();
      this.initProductVariants();
      this.initStickyAtc();
      this.initFacets();
      this.initFavourites();
      this.initDeliveryCountdown();
      this.updateCartCount();
      this.initAgeGate();
      this.syncAgeCheckoutAttributes();
    },

    cacheDom() {
      this.body = document.body;
      this.header = document.querySelector('[data-site-header]');
      this.postcodeGate = document.querySelector('[data-postcode-gate]');
      this.cartDrawer = document.querySelector('[data-cart-drawer]');
      this.navDrawer = document.querySelector('[data-nav-drawer]');
    },

    bindGlobal() {
      document.addEventListener('click', (e) => {
        const openCart = e.target.closest('[data-open-cart]');
        if (openCart) {
          e.preventDefault();
          this.openCart();
        }

        const closeCart = e.target.closest('[data-close-cart]');
        if (closeCart) {
          e.preventDefault();
          this.closeCart();
        }

        const openNav = e.target.closest('[data-open-nav]');
        if (openNav) {
          e.preventDefault();
          this.openNav();
        }

        const closeNav = e.target.closest('[data-close-nav]');
        if (closeNav) {
          e.preventDefault();
          this.closeNav();
        }

        const changePostcode = e.target.closest('[data-change-postcode]');
        if (changePostcode) {
          e.preventDefault();
          this.showPostcodeGate(true);
        }

        const justBrowsing = e.target.closest('[data-just-browsing]');
        if (justBrowsing) {
          e.preventDefault();
          this.continueBrowsing();
        }

        const addToCart = e.target.closest('[data-add-to-cart]');
        if (addToCart) {
          e.preventDefault();
          this.handleAddToCart(addToCart);
        }
      });

      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-cart-checkout-form]');
        if (!form) return;
        const submitter = e.submitter;
        const isCheckout = submitter && (submitter.getAttribute('name') === 'checkout' || submitter.hasAttribute('data-checkout-btn'));
        if (!isCheckout && submitter && submitter.getAttribute('name') === 'update') return;

        if (isCheckout || (!submitter && form.querySelector('[name="checkout"]'))) {
          const minOrder = Math.max(0, parseFloat(this.settings.minimumOrderValue || '0') * 100);
          if (minOrder <= 0) return;
          const totalEl = document.querySelector('[data-cart-total-cents]');
          let total = totalEl ? parseInt(totalEl.dataset.cartTotalCents, 10) || 0 : 0;
          /* Prefer live cart total from subtotal data if available after AJAX */
          if (this._cartTotalCents != null) total = this._cartTotalCents;
          if (total < minOrder) {
            e.preventDefault();
            this.showToast(this.settings.minOrderBlocked || `Minimum order is £${this.settings.minimumOrderValue || '0'}`);
            this.updateProgressBars({ total_price: total });
          }
        }
      });

      window.addEventListener('scroll', () => {
        if (!this.header) return;
        this.header.classList.toggle('is-scrolled', window.scrollY > 8);
      }, { passive: true });
    },

    /* ------------------------------------------------------------------ */
    /* Postcode gate                                                      */
    /* ------------------------------------------------------------------ */
    getAllowedPrefixes() {
      const raw = (this.settings.postcodePrefixes || 'LA13,LA14').toUpperCase();
      return raw.split(',').map((p) => p.trim()).filter(Boolean);
    },

    getStoredPostcode() {
      try {
        return localStorage.getItem(STORAGE_KEY) || '';
      } catch (e) {
        return '';
      }
    },

    setStoredPostcode(postcode) {
      try {
        localStorage.setItem(STORAGE_KEY, postcode);
        localStorage.removeItem(BROWSE_KEY);
      } catch (e) { /* ignore */ }
      this.updatePostcodeIndicators(postcode);
    },

    isBrowsingOnly() {
      try {
        return localStorage.getItem(BROWSE_KEY) === '1';
      } catch (e) {
        return false;
      }
    },

    continueBrowsing() {
      try {
        localStorage.setItem(BROWSE_KEY, '1');
      } catch (e) { /* ignore */ }
      this.hidePostcodeGate();
    },

    normalisePostcode(value) {
      return (value || '').toUpperCase().replace(/\s+/g, ' ').trim();
    },

    extractPrefix(postcode) {
      const cleaned = postcode.replace(/\s+/g, '').toUpperCase();
      const match = cleaned.match(/^([A-Z]{1,2}\d{1,2})/);
      return match ? match[1] : '';
    },

    isPostcodeAllowed(postcode) {
      const prefix = this.extractPrefix(postcode);
      if (!prefix) return false;
      return this.getAllowedPrefixes().some((allowed) => prefix === allowed || prefix.startsWith(allowed));
    },

    initPostcodeGate() {
      if (!this.settings.postcodeGateEnabled) {
        this.body.classList.remove('postcode-locked');
        document.documentElement.classList.add('postcode-ready');
        if (this.postcodeGate) this.postcodeGate.classList.remove('is-visible');
        return;
      }

      const stored = this.getStoredPostcode();
      if (stored && this.isPostcodeAllowed(stored)) {
        this.updatePostcodeIndicators(stored);
        this.hidePostcodeGate();
        document.documentElement.classList.add('postcode-ready');
      } else if (this.isBrowsingOnly()) {
        this.hidePostcodeGate();
        document.documentElement.classList.add('postcode-ready');
      } else {
        this.showPostcodeGate(false);
      }

      const form = document.querySelector('[data-postcode-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.checkPostcode(form);
        });
      }

      // Delivery eligibility checker on delivery page
      const checker = document.querySelector('[data-delivery-checker-form]');
      if (checker) {
        checker.addEventListener('submit', (e) => {
          e.preventDefault();
          this.checkPostcode(checker, true);
        });
      }
    },

    checkPostcode(form, persistOnlyIfValid = false) {
      const input = form.querySelector('[name="postcode"]');
      const message = form.parentElement.querySelector('[data-postcode-message]')
        || form.querySelector('[data-postcode-message]');
      const postcode = this.normalisePostcode(input && input.value);

      if (!postcode || postcode.length < 3) {
        this.showPostcodeMessage(message, false, 'Please enter a valid UK postcode.');
        if (input) input.classList.add('field__input--error');
        return;
      }

      if (input) input.classList.remove('field__input--error');

      const allowed = this.isPostcodeAllowed(postcode);
      if (allowed) {
        this.setStoredPostcode(postcode);
        this.showPostcodeMessage(
          message,
          true,
          this.settings.postcodeAvailableMessage || 'Great news! We deliver to your area.'
        );
        if (!persistOnlyIfValid) {
          setTimeout(() => this.hidePostcodeGate(), 700);
        }
      } else {
        if (!persistOnlyIfValid) {
          try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
        }
        this.showPostcodeMessage(
          message,
          false,
          this.settings.postcodeUnavailableMessage || "Sorry, we don't deliver to that postcode yet."
        );
      }
    },

    showPostcodeMessage(el, success, text) {
      if (!el) return;
      el.textContent = text;
      el.classList.remove('is-success', 'is-error');
      el.classList.add(success ? 'is-success' : 'is-error');
    },

    hidePostcodeGate() {
      if (!this.postcodeGate) return;
      this.postcodeGate.classList.remove('is-visible');
      this.body.classList.remove('postcode-locked');
      document.documentElement.classList.add('postcode-ready');
      /* Age gate may need to appear now that postcode is cleared */
      const needsAge = document.documentElement.classList.contains('age-gate-needed')
        && !document.documentElement.classList.contains('age-verified');
      if (needsAge) {
        const gate = this.ageGate || document.querySelector('[data-age-gate]');
        if (gate) {
          this.ageGate = gate;
          this.showAgeGate();
        }
      }
    },

    showPostcodeGate(force) {
      if (!this.postcodeGate) return;
      document.documentElement.classList.remove('postcode-ready');
      this.postcodeGate.classList.add('is-visible');
      this.body.classList.add('postcode-locked');
      if (force) {
        const msg = this.postcodeGate.querySelector('[data-postcode-message]');
        if (msg) {
          msg.classList.remove('is-success', 'is-error');
          msg.textContent = '';
        }
      }
    },

    updatePostcodeIndicators(postcode) {
      document.querySelectorAll('[data-postcode-display]').forEach((el) => {
        el.textContent = postcode || '—';
      });
      document.querySelectorAll('[data-postcode-wrap]').forEach((el) => {
        el.hidden = !postcode;
      });
      document.querySelectorAll('[data-postcode-fallback]').forEach((el) => {
        el.hidden = !!postcode;
      });
    },

    /* ------------------------------------------------------------------ */
    /* Header / nav                                                       */
    /* ------------------------------------------------------------------ */
    initHeader() {
      const stored = this.getStoredPostcode();
      if (stored) this.updatePostcodeIndicators(stored);
    },

    openNav() {
      if (!this.navDrawer) return;
      this.navDrawer.classList.add('is-open');
      this.body.style.overflow = 'hidden';
    },

    closeNav() {
      if (!this.navDrawer) return;
      this.navDrawer.classList.remove('is-open');
      this.body.style.overflow = '';
    },

    /* ------------------------------------------------------------------ */
    /* Cart                                                               */
    /* ------------------------------------------------------------------ */
    openCart() {
      if (!this.cartDrawer) {
        window.location.href = '/cart';
        return;
      }
      this.refreshCartDrawer().then(() => {
        this.cartDrawer.classList.add('is-open');
        this.body.style.overflow = 'hidden';
      });
    },

    closeCart() {
      if (!this.cartDrawer) return;
      this.cartDrawer.classList.remove('is-open');
      this.body.style.overflow = '';
    },

    initCartDrawer() {
      if (!this.cartDrawer) return;

      this.cartDrawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn) {
          e.preventDefault();
          const key = qtyBtn.dataset.lineKey;
          const delta = parseInt(qtyBtn.dataset.cartQty, 10);
          const input = this.cartDrawer.querySelector(`[data-line-qty="${key}"]`);
          const current = input ? parseInt(input.value, 10) : 1;
          this.updateCartLine(key, Math.max(0, current + delta));
        }

        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this.updateCartLine(removeBtn.dataset.cartRemove, 0);
        }
      });
    },

    async handleAddToCart(btn) {
      const form = btn.closest('form')
        || document.querySelector('[data-product-form]')
        || document.getElementById('ProductForm');
      let variantId = btn.dataset.variantId;
      let quantity = 1;

      if (form) {
        const idInput = form.querySelector('[name="id"]');
        const qtyInput = form.querySelector('[name="quantity"], [data-qty-input]');
        if (idInput) variantId = idInput.value;
        if (qtyInput) quantity = parseInt(qtyInput.value, 10) || 1;
      }

      if (!variantId) return;

      const original = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] })
        });

        if (!res.ok) throw new Error('Add failed');

        btn.textContent = this.settings.addedLabel || 'Added';
        await this.updateCartCount();
        this.refreshCartDrawer();
        this.showToast(this.settings.itemAddedMessage || 'Item added to basket');
        this.syncAgeCheckoutAttributes();

        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 1200);
      } catch (err) {
        btn.textContent = 'Error';
        setTimeout(() => {
          btn.textContent = original;
          btn.disabled = false;
        }, 1500);
      }
    },

    showToast(message) {
      let toast = document.querySelector('[data-cart-toast]');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-toast';
        toast.setAttribute('data-cart-toast', '');
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        document.body.appendChild(toast);
      }

      toast.textContent = message;
      toast.classList.add('is-visible');

      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, 2800);
    },

    async updateCartLine(key, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        await this.refreshCartDrawer();
        await this.updateCartCount();
        this.syncAgeCheckoutAttributes();
      } catch (e) {
        console.error(e);
      }
    },

    async refreshCartDrawer() {
      if (!this.cartDrawer) return;
      try {
        const res = await fetch('/cart?section_id=cart-drawer');
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-drawer]');
        if (fresh) {
          this.cartDrawer.innerHTML = fresh.innerHTML;
        }
        this.updateProgressBars();
        this.syncAgeCheckoutAttributes();
      } catch (e) {
        // Fallback: fetch cart JSON and rely on page reload path
        console.error(e);
      }
    },

    async updateCartCount() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        this._cartTotalCents = cart.total_price;
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
        document.querySelectorAll('[data-cart-total-cents]').forEach((el) => {
          el.dataset.cartTotalCents = String(cart.total_price);
        });
        this.updateProgressBars(cart);
      } catch (e) { /* ignore */ }
    },

    updateProgressBars(cart) {
      const minOrder = Math.max(0, parseFloat(this.settings.minimumOrderValue || '0') * 100);
      const rateThreshold = parseFloat(this.settings.deliveryRateThreshold || '10') * 100;
      const feeUnder = this.settings.deliveryFeeUnder || '1.49';
      const feeOver = this.settings.deliveryFeeOver || '1.00';
      const minLabel = this.settings.minOrderReached || 'Minimum order reached';
      const blockedTpl = this.settings.minOrderBlocked || 'Minimum order is £{{ amount }}';
      const enforceMin = minOrder > 0;

      const formatMoney = (cents) => {
        const amount = (Number(cents) / 100).toFixed(2);
        const fmt = this.settings.moneyFormat || '£{{amount}}';
        if (fmt.indexOf('{{amount}}') !== -1) return fmt.replace('{{amount}}', amount);
        return `£${amount}`;
      };

      const apply = (total) => {
        const belowMin = enforceMin && total < minOrder;

        document.querySelectorAll('[data-min-order-progress]').forEach((el) => {
          if (!enforceMin) {
            el.hidden = true;
            return;
          }
          el.hidden = false;
          const pct = Math.min(100, (total / minOrder) * 100);
          const fill = el.querySelector('[data-progress-fill]');
          const label = el.querySelector('[data-progress-label]');
          el.classList.toggle('cart-progress--warn', belowMin);
          if (fill) fill.style.width = pct + '%';
          if (label) {
            if (!belowMin) {
              label.textContent = minLabel;
            } else {
              const remaining = formatMoney(minOrder - total);
              label.textContent = `Spend ${remaining} more for minimum order`;
            }
          }
        });

        document.querySelectorAll('[data-delivery-rate-progress]').forEach((el) => {
          const pct = Math.min(100, (total / rateThreshold) * 100);
          const fill = el.querySelector('[data-progress-fill]');
          const label = el.querySelector('[data-progress-label]');
          const thresholdPounds = String(this.settings.deliveryRateThreshold || '10');
          if (fill) fill.style.width = pct + '%';
          if (label) {
            if (total >= rateThreshold) {
              label.textContent = `Delivery £${feeOver} on orders over £${thresholdPounds}`;
            } else {
              label.textContent = `Delivery £${feeUnder} under £${thresholdPounds}`;
            }
          }
        });

        document.querySelectorAll('[data-checkout-btn]').forEach((btn) => {
          if (!enforceMin) {
            btn.disabled = false;
            btn.setAttribute('aria-disabled', 'false');
            return;
          }
          btn.disabled = belowMin;
          btn.setAttribute('aria-disabled', belowMin ? 'true' : 'false');
        });

        document.querySelectorAll('[data-min-order-msg]').forEach((el) => {
          if (!enforceMin) {
            el.hidden = true;
            el.classList.add('is-met');
            return;
          }
          if (belowMin) {
            const remaining = formatMoney(minOrder - total);
            const amount = String(this.settings.minimumOrderValue || '0');
            el.hidden = false;
            el.classList.remove('is-met');
            el.textContent = `${blockedTpl.replace('{{ amount }}', amount).replace('{{amount}}', amount)} — Spend ${remaining} more for minimum order`;
          } else {
            el.hidden = true;
            el.classList.add('is-met');
          }
        });
      };

      if (cart) {
        this._cartTotalCents = cart.total_price;
        apply(cart.total_price);
      } else {
        const totalEl = document.querySelector('[data-cart-total-cents]');
        if (totalEl) {
          const total = parseInt(totalEl.dataset.cartTotalCents, 10) || 0;
          this._cartTotalCents = total;
          apply(total);
        }
      }
    },

    /* ------------------------------------------------------------------ */
    /* Quantity                                                           */
    /* ------------------------------------------------------------------ */
    initQuantityControls() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-btn]');
        if (!btn) return;
        e.preventDefault();
        const wrap = btn.closest('[data-qty]');
        const input = wrap && wrap.querySelector('[data-qty-input]');
        if (!input) return;
        const delta = btn.dataset.qtyBtn === 'plus' ? 1 : -1;
        const min = parseInt(input.min || '1', 10);
        const max = parseInt(input.max || '99', 10);
        const next = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 1) + delta));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },

    /* ------------------------------------------------------------------ */
    /* Product tabs / sticky ATC                                          */
    /* ------------------------------------------------------------------ */
    initProductTabs() {
      document.querySelectorAll('[data-product-tabs]').forEach((tabs) => {
        tabs.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-tab]');
          if (!btn) return;
          const id = btn.dataset.tab;
          tabs.querySelectorAll('[data-tab]').forEach((b) => b.classList.toggle('is-active', b === btn));
          tabs.querySelectorAll('[data-tab-panel]').forEach((p) => {
            p.classList.toggle('is-active', p.dataset.tabPanel === id);
          });
        });
      });
    },

    initProductVariants() {
      const form = document.querySelector('[data-product-form]') || document.getElementById('ProductForm');
      if (!form) return;

      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add-to-cart]');
        if (btn && !btn.disabled) this.handleAddToCart(btn);
      });

      const jsonEl = document.querySelector('[data-product-variants]');
      if (!jsonEl) return;

      let product;
      try {
        product = JSON.parse(jsonEl.textContent);
      } catch (e) {
        return;
      }
      if (!product || !product.variants) return;

      const optionSelects = Array.from(form.querySelectorAll('[data-option-select]'));
      if (!optionSelects.length) return;

      const moneyFormat = this.settings.moneyFormat || '£{{amount}}';
      const formatMoney = (cents) => {
        const amount = (Number(cents) / 100).toFixed(2);
        if (moneyFormat.indexOf('{{amount}}') !== -1) {
          return moneyFormat.replace('{{amount}}', amount);
        }
        if (moneyFormat.indexOf('{{amount_no_decimals}}') !== -1) {
          return moneyFormat.replace('{{amount_no_decimals}}', String(Math.round(Number(cents) / 100)));
        }
        return `£${amount}`;
      };

      const syncVariant = () => {
        const selected = optionSelects.map((sel) => sel.value);
        const variant = product.variants.find((v) =>
          selected.every((val, i) => String(v.options[i]) === String(val))
        ) || product.variants[0];

        if (!variant) return;

        const idInput = form.querySelector('[name="id"], [data-variant-id]');
        if (idInput) idInput.value = variant.id;

        form.querySelectorAll('[data-add-to-cart]').forEach((btn) => {
          btn.dataset.variantId = variant.id;
          btn.disabled = !variant.available;
          btn.textContent = variant.available
            ? (btn.dataset.availableLabel || 'Add to basket')
            : (this.settings.soldOutLabel || 'Sold out');
        });

        const sticky = document.querySelector('[data-sticky-atc]');
        if (sticky) {
          const priceEl = sticky.querySelector('[data-sticky-price]');
          if (priceEl) priceEl.textContent = formatMoney(variant.price);
          const stickyBtn = sticky.querySelector('[data-add-to-cart]');
          if (stickyBtn) {
            stickyBtn.dataset.variantId = variant.id;
            stickyBtn.disabled = !variant.available;
            stickyBtn.textContent = variant.available
              ? (stickyBtn.dataset.availableLabel || 'Add to basket')
              : (this.settings.soldOutLabel || 'Sold out');
          }
        }

        document.querySelectorAll('[data-product-price]').forEach((el) => {
          el.textContent = formatMoney(variant.price);
        });

        document.querySelectorAll('[data-product-compare]').forEach((el) => {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            el.textContent = formatMoney(variant.compare_at_price);
            el.hidden = false;
          } else {
            el.hidden = true;
          }
        });
      };

      optionSelects.forEach((sel) => {
        sel.addEventListener('change', syncVariant);
      });
      syncVariant();
    },

    initStickyAtc() {
      const sticky = document.querySelector('[data-sticky-atc]');
      const target = document.querySelector('[data-product-buy-row]')
        || document.querySelector('[data-product-form]')
        || document.getElementById('ProductForm');
      if (!sticky) return;

      /* Escape section containing blocks so position:fixed always works */
      if (sticky.parentElement !== document.body) {
        document.body.appendChild(sticky);
      }

      if (!target) {
        sticky.classList.remove('is-hidden');
        return;
      }

      const update = (entry) => {
        /* Hide sticky only when buy controls are in view */
        sticky.classList.toggle('is-hidden', entry.isIntersecting);
      };

      const observer = new IntersectionObserver(
        ([entry]) => update(entry),
        { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
      );
      observer.observe(target);

      /* Initial state: show unless buy row is already intersecting */
      const rect = target.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.9 && rect.bottom > 0;
      sticky.classList.toggle('is-hidden', inView);
    },

    /* ------------------------------------------------------------------ */
    /* Facets                                                             */
    /* ------------------------------------------------------------------ */
    initFacets() {
      document.querySelectorAll('[data-open-facets]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const drawer = document.querySelector('[data-facets-drawer]');
          if (drawer) drawer.classList.add('is-open');
        });
      });
      document.querySelectorAll('[data-close-facets]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const drawer = document.querySelector('[data-facets-drawer]');
          if (drawer) drawer.classList.remove('is-open');
        });
      });
    },

    /* ------------------------------------------------------------------ */
    /* Favourites                                                         */
    /* ------------------------------------------------------------------ */
    getFavourites() {
      try {
        return JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
      } catch (e) {
        return [];
      }
    },

    setFavourites(ids) {
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify(ids));
      } catch (e) { /* ignore */ }
    },

    initFavourites() {
      const favs = this.getFavourites();
      document.querySelectorAll('[data-favourite]').forEach((btn) => {
        const id = btn.dataset.favourite;
        btn.classList.toggle('is-active', favs.includes(id));
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          let list = this.getFavourites();
          if (list.includes(id)) {
            list = list.filter((x) => x !== id);
            btn.classList.remove('is-active');
          } else {
            list.push(id);
            btn.classList.add('is-active');
          }
          this.setFavourites(list);
        });
      });
    },

    /* ------------------------------------------------------------------ */
    /* Delivery countdown                                                 */
    /* ------------------------------------------------------------------ */
    initDeliveryCountdown() {
      const els = document.querySelectorAll('[data-delivery-countdown]');
      if (!els.length) return;

      const cutoffHour = parseInt(this.settings.deliveryCutoffHour || '20', 10);

      const tick = () => {
        const now = new Date();
        const cutoff = new Date(now);
        cutoff.setHours(cutoffHour, 0, 0, 0);

        els.forEach((el) => {
          if (now >= cutoff) {
            el.textContent = this.settings.cutoffPassed || 'Order tomorrow for next-day delivery';
            return;
          }
          const diff = cutoff - now;
          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          const s = Math.floor((diff % 60000) / 1000);
          const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
          el.innerHTML = `Order within <span class="countdown">${time}</span> for same-day delivery`;
        });
      };

      tick();
      setInterval(tick, 1000);
    },

    /* ------------------------------------------------------------------ */
    /* Age verification                                                   */
    /* ------------------------------------------------------------------ */
    initAgeGate() {
      const ctx = window.ageGateContext || {};
      const enabled = ctx.enabled !== false && this.settings.ageVerificationEnabled !== false;
      if (!enabled) return;

      this.ageGate = document.querySelector('[data-age-gate]');
      if (!this.ageGate) return;

      const needsGate = this.detectAgeGateNeeded(ctx);
      if (!needsGate) return;

      document.documentElement.classList.add('age-gate-needed');
      document.body.classList.add('age-gate-required');

      if (this.isAgeVerified()) {
        this.unlockAgeGate();
        return;
      }

      this.showAgeGate();

      const yesBtn = this.ageGate.querySelector('[data-age-yes]');
      const noBtn = this.ageGate.querySelector('[data-age-no]');
      if (yesBtn) yesBtn.addEventListener('click', () => this.confirmAge());
      if (noBtn) noBtn.addEventListener('click', () => this.declineAge());
    },

    detectAgeGateNeeded(ctx) {
      if (ctx.needsGate === true || ctx.requireAgeGate === true) return true;

      const handles = this.normalizeAgeList(ctx.handles || this.settings.alcoholCollectionHandles || 'spirits,beer-cider,wine,alcohol,beer,cider');
      const tags = this.normalizeAgeList(ctx.tags || this.settings.alcoholProductTags || 'alcohol,spirits,beer,cider,wine');
      const keywords = ['spirit', 'beer', 'cider', 'wine', 'alcohol', 'lager', 'vodka', 'gin', 'whisky', 'whiskey', 'rum'];

      const titleHit = (title) => {
        const t = String(title || '').toLowerCase();
        return keywords.some((k) => t.indexOf(k) !== -1);
      };

      if (ctx.pageType === 'collection') {
        return handles.indexOf(String(ctx.collectionHandle || '').toLowerCase()) !== -1 || titleHit(ctx.collectionTitle);
      }

      if (ctx.pageType === 'product') {
        const ph = ctx.productCollectionHandles || [];
        const pt = ctx.productTags || [];
        return ph.some((h) => handles.indexOf(String(h).toLowerCase()) !== -1)
          || pt.some((t) => tags.indexOf(String(t).toLowerCase()) !== -1)
          || tags.indexOf(String(ctx.productType || '').toLowerCase()) !== -1
          || titleHit(ctx.productType);
      }

      return false;
    },

    normalizeAgeList(str) {
      return String(str || '')
        .toLowerCase()
        .replace(/\//g, '-')
        .replace(/\s+/g, '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    },

    isAgeVerified() {
      try {
        const raw = localStorage.getItem('tydb_age_verified');
        if (!raw) return false;
        const data = JSON.parse(raw);
        if (data && data.expires && Date.now() < data.expires) return true;
        localStorage.removeItem('tydb_age_verified');
        return false;
      } catch (e) {
        return false;
      }
    },

    confirmAge() {
      const days = parseInt((window.ageGateContext && window.ageGateContext.days) || this.settings.ageVerificationDays || 30, 10);
      const expires = Date.now() + days * 24 * 60 * 60 * 1000;
      try {
        localStorage.setItem('tydb_age_verified', JSON.stringify({ verified: true, expires }));
        document.cookie = `tydb_age_verified=1; path=/; max-age=${days * 24 * 60 * 60}; SameSite=Lax`;
      } catch (e) { /* ignore */ }
      this.unlockAgeGate();
    },

    declineAge() {
      try {
        localStorage.removeItem('tydb_age_verified');
        document.cookie = 'tydb_age_verified=; path=/; max-age=0; SameSite=Lax';
      } catch (e) { /* ignore */ }
      const home = (window.ageGateContext && window.ageGateContext.homeUrl) || this.settings.homeUrl || '/';
      window.location.href = home;
    },

    showAgeGate() {
      document.documentElement.classList.remove('age-verified');
      document.documentElement.classList.add('age-gate-needed');
      document.body.classList.add('age-locked', 'age-gate-required');
      if (this.ageGate) this.ageGate.classList.add('is-visible');
    },

    unlockAgeGate() {
      document.documentElement.classList.add('age-verified');
      document.body.classList.remove('age-locked', 'age-gate-required');
      if (this.ageGate) this.ageGate.classList.remove('is-visible');
    },

    /* Keep age-restriction attributes on the cart so they appear at checkout */
    syncAgeCheckoutAttributes() {
      const hasWarning = !!document.querySelector('[data-age-notice], [data-age-checkout-warning]');
      const notice = (this.settings.ageCheckoutNotice
        || (window.themeSettings && window.themeSettings.ageCheckoutNotice)
        || 'ID may be requested upon delivery for age-restricted items.');

      const attributes = hasWarning
        ? {
            'Age-restricted order': notice,
            'Contains alcohol': 'Yes'
          }
        : {
            'Age-restricted order': '',
            'Contains alcohol': ''
          };

      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes })
      }).catch(() => {});
    }
  };

  document.addEventListener('DOMContentLoaded', () => Theme.init());
  window.TYDB = Theme;
})();
